import useFormat from '@business/utils/hooks/useFormat';
import { Box, Flex } from '@chakra-ui/react';
import { Accommodation, Addon } from '@data/models';
import ReservationItem from '@data/models/ReservationItem';
import { useStore } from '@data/other/withStore';
import BEIcon from '@molecules/BEIcon';
import BEPills from '@molecules/BEPills';
import Text from '@molecules/Text';
import { t } from 'i18next';
import { observer } from 'mobx-react';
import { useCallback, useMemo } from 'react';
import { StyledShoppingItemContainer, TrashButton } from './StyledShoppingItem';
interface ShoppingItemProps {
  item: ReservationItem;
}

const ShoppingItem = ({ item }: ShoppingItemProps) => {
  const {
    property: { accommodations, cart },
  } = useStore();
  const { currency } = useFormat();

  const onClickRemove = useCallback(() => {
    cart.remove(item);
  }, [cart, item]);

  const currencyRate = accommodations.model?.currency_rate || 1;

  const acc = accommodations.model?.items.find(
    (acc) => acc.id === item.model?.item?.id
  );

  const descriptionText = useMemo(() => {
    if (!item.model) return '';
    const { amount, type } = item.model;
    const text =
      type === 'addon'
        ? t(`addon-${(item.model.item as Addon).chargeType}`).toLowerCase()
        : (item.model.item as Accommodation).packageName;
    return amount <= 1 ? text : `${amount}x ${text}`;
  }, [item, item.model]);

  const title = useMemo(() => {
    if (!item.model) return '';
    if (item.model.type === 'accommodation') {
      const name = acc?.name || item.model.item.name;
      if (item.model.unit) {
        return `${name} - ${item.model.unit}`;
      } else {
        return name;
      }
    } else {
      return item.model.item.name.getText();
    }
  }, [item, item.model, acc?.name]);

  return (
    <StyledShoppingItemContainer>
      <Flex w="100%" justifyContent="space-between">
        <Flex gap="4px">
          {item.model?.type === 'addon' && (
            <Box position="relative" top="-2px">
              <BEIcon name="basket" />
            </Box>
          )}
          <Flex direction="column">
            <Text type="h4">{title}</Text>
            <Text
              type="card-body"
              color="neutral"
              darkness="700"
              className="shopping-item-description"
            >
              {descriptionText}
            </Text>
            {item.model?.type === 'accommodation' && (
              <Box mt="8px">
                <Flex gap="12px">
                  <BEPills iconRight="user">{item.model?.adults}</BEPills>
                  <BEPills iconRight="child-reaching">
                    {item.model?.kids}
                  </BEPills>
                </Flex>
              </Box>
            )}
          </Flex>
        </Flex>
        <Flex
          direction="column"
          alignItems={'end'}
          minH="76px"
          justifyContent={'space-between'}
        >
          <Text type="card-body">
            {currency(item.getPrice() * currencyRate)}
          </Text>
          <Box onClick={onClickRemove}>
            <TrashButton
              aria-label="delete"
              variant="outline"
              icon={<BEIcon name="trash-can" />}
            />
          </Box>
        </Flex>
      </Flex>
    </StyledShoppingItemContainer>
  );
};

export default observer(ShoppingItem);
